import React, { useState, FC, useEffect, Fragment, useContext } from 'react';
import { IconButton, TableRow } from '@mui/material';
import { DataTableCell, DataTableValue } from 'components/shared/data-table/data-table.componet';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTableTextField from 'components/shared/data-table/data-table-text-field.component';
import DataTableCodeAutocomplete from 'components/shared/data-table/data-table-code-autocomplete.component';
import DataTableAutocomplete, {
    DataTableAutocompleteOption,
} from 'components/shared/data-table/data-table-autocomplete.component';
import { formatCurrency } from 'utils/format';
import { useFormik } from 'formik';
import { isNumeric } from 'utils/validations';
import { ActionGridInput } from 'models/order-input/shared';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import isEqual from 'lodash/isEqual';
import DiscountButton from '../shared/discount-button.component';
import { emptyActionInput, isActionRowEmpty } from './form-helper';
import { ActionMaterial, OrderLabAction } from 'models/order-input/lab-order';
import { MaterialMacroOption } from 'models/material-macro';
import { CurrencyContext } from 'context/currency';
import { actionPriceLimit, quantityFollowsTeeth, toNumber } from '../actions-and-materials-helper';

const vatOptions = [{ key: '0' }, { key: '6' }, { key: '12' }, { key: '25' }];

type ActionsDataGridRowProps = {
    action: ActionGridInput;
    priceList: OrderLabAction[];
    onChange?: (
        action: ActionGridInput,
        // Absent entirely on the SIMPLE client price list, which does not select the field.
        actionMaterials: ActionMaterial[] | undefined,
        isActionMaterialRequired: boolean,
        materialMacroOptions?: MaterialMacroOption[],
    ) => void;
    isPlaceholderRow?: boolean;
    warranty?: boolean;
    isTioTandvard?: boolean;
    isActionBasedOrder: boolean;
    disableForActionBased?: boolean;
    defaultQuantity?: number;
    /** The quantity of a per-tooth action: the construction's teeth, as the server counts them. */
    perToothQuantity?: number;
    dataTest?: string;
};
const ActionsDataGridRow: FC<ActionsDataGridRowProps> = ({
    action,
    priceList,
    onChange,
    isPlaceholderRow = false,
    warranty,
    isTioTandvard,
    isActionBasedOrder,
    disableForActionBased = false,
    defaultQuantity = 1,
    perToothQuantity,
    dataTest,
}) => {
    const { t: translate } = useTranslation(['orders', 'actions-materials']);
    const { currency } = useContext(CurrencyContext);
    const [selectedCell, setSelectedCell] = useState<string>();
    const [selectedAutocompleteItem, setSelectedAutocompleteItem] = useState<DataTableAutocompleteOption | null>(null);
    const [totalExclVat, setTotalExclVat] = useState<string>();
    const [totalIncVat, setTotalIncVat] = useState<string>();
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [hasChangedOnce, setHasChangedOnce] = useState(false);

    const validate = (formData: ActionGridInput) => {
        const errors: Record<string, string> = {};
        if (isActionRowEmpty(formData)) {
            return errors;
        }
        // A new line needs an action from the catalogue; a stored line keeps its own code
        // and description even when they are not (or no longer) in the catalogue (DD-2506).
        if (formData.description && !formData.actionId && !formData.rowId) {
            errors.actionId = 'ERROR';
        }
        if (!formData.description) {
            errors.description = 'ERROR';
        }
        if (!isNumeric(formData.quantity)) {
            errors.quantity = 'ERROR';
        }
        if (!isNumeric(formData.unitPrice)) {
            errors.unitPrice = 'ERROR';
        }
        if (
            disableForActionBased &&
            !formData.quotable &&
            isNumeric(formData.unitPrice) &&
            formData.originalPrice !== undefined
        ) {
            if (parseFloat(formData.unitPrice) > formData.originalPrice) {
                errors.unitPrice = 'ERROR';
            }
        }
        return errors;
    };

    const formik = useFormik<ActionGridInput>({
        initialValues: emptyActionInput(),
        validate,
        onSubmit: (values) => {
            if (onChange) {
                onChange(values, [], false);
            }
        },
    });

    useEffect(() => {
        const values = {
            _id: action._id,
            // The stored row this grid row edits; the grid replaces a row with what this form
            // emits, so the id has to be carried through or the save treats it as a new row.
            rowId: action.rowId,
            actionId: action.actionId || '',
            actionCode: action.actionCode,
            description: action.description,
            quantity: action.quantity?.toString() || '',
            basePrice: action.basePrice || 0,
            unitPrice: action.unitPrice || '',
            tiotandvardPrice: action.tiotandvardPrice || action.unitPrice || '',
            discountedPrice: action.discountedPrice,
            customerSpecificTiotandvardPrice: action.customerSpecificTiotandvardPrice,
            price: action.price,
            vatPercent: action.vatPercent || '',
            priceWithVat: action.priceWithVat || 0,
            priceWithoutVat: action.priceWithoutVat || 0,
            category: action.category || '',
            isValid: action.isValid || true,
            originalPrice: action.originalPrice || 0,
            originalVat: action.originalVat || 0,
            quotable: action.quotable,
            costPerTooth: action.costPerTooth,
        };

        if (!isEqual(formik.values, values)) {
            formik.setValues(values, false);
            setHasChangedOnce(!isActionRowEmpty(values));
            if (action._id) {
                setSelectedAutocompleteItem({
                    id: action._id || '',
                    key: action.actionCode || '',
                    value: action.description || '',
                });
            } else {
                setSelectedAutocompleteItem(null);
            }
        }
    }, [action, warranty]);

    useEffect(() => {
        // Handle total price caluclations
        try {
            setTotalExclVat(formatCurrency(0, currency));
            setTotalIncVat(formatCurrency(0, currency));
            formik.setFieldValue('priceWithoutVat', 0, false);
            formik.setFieldValue('priceWithVat', 0, false);

            if (warranty) {
                formik.setFieldValue('unitPrice', 0, false);
                formik.setFieldValue('tiotandvardPrice', 0, false);
                return;
            }

            let total = 0;
            const newValues = { ...formik.values };
            if (formik.values.unitPrice && formik.values.quantity) {
                const priceNum = parseFloat(formik.values.unitPrice.replace(',', '.'));
                const quantityNum = parseFloat(formik.values.quantity.replace(',', '.'));
                total = priceNum * quantityNum;
                if (isNaN(total)) {
                    total = 0;
                }
                setTotalExclVat(formatCurrency(total, currency));
                setTotalIncVat(formatCurrency(total, currency));
                newValues.priceWithoutVat = total;
                newValues.priceWithVat = total;
            }

            if (formik.values.vatPercent && formik.values.vatPercent !== '0') {
                const vatNum = parseFloat(formik.values.vatPercent.replace(',', '.')) / 100 + 1;
                setTotalIncVat(formatCurrency(total * vatNum, currency));
                newValues.priceWithVat = total * vatNum;
                formik.setFieldValue('priceWithVat', total * vatNum, false);
            }

            formik.setFieldValue('priceWithoutVat', total, false);
            formik.setFieldValue('priceWithVat', total, false);
            if (onChange && !isActionRowEmpty(newValues)) {
                const rowErrors = validate(newValues);
                const isRowValid = Object.keys(rowErrors).length === 0;
                onChange({ ...newValues, isValid: isRowValid }, [], false);
            }
        } catch (error) {
            setTotalExclVat('0');
            setTotalIncVat('0');
        }
    }, [formik.values.quantity, formik.values.unitPrice, formik.values.vatPercent, warranty]);

    const isCellSelected = (colId: string) => {
        return selectedCell === colId;
    };

    const handleActionSelectChanged = (value: DataTableAutocompleteOption | null) => {
        setSelectedAutocompleteItem(value);
        if (priceList && value) {
            const action = priceList.find((action) => action.action === value?.id) as any;
            // For tiotandvard orders: prefer customer-specific tiotandvard price, fall back to lab tiotandvard price, then price(resolved in backend with getActionPrice).
            let unitPrice = isTioTandvard
                ? action.customerSpecificTiotandvardPrice || action.tiotandvardPrice || action.price
                : action.price;
            // For simple (non-action-based) tiotandvard orders with no customer-specific tiotandvard price, use the discounted client price if set.
            if (
                isTioTandvard &&
                !isActionBasedOrder &&
                !action.customerSpecificTiotandvardPrice &&
                action.discountedPrice
            ) {
                unitPrice = action.discountedPrice;
            }
            if (action) {
                const newValues = {
                    ...formik.values,
                    _id: action._id,
                    actionId: action.action || '',
                    actionCode: action.actionCode || '',
                    description: action.description,
                    // A per-tooth action-set action counts the teeth; the server ignores any other quantity (DD-3598).
                    quantity:
                        quantityFollowsTeeth(action, disableForActionBased) && perToothQuantity
                            ? String(perToothQuantity)
                            : formik.values.quantity || String(defaultQuantity),
                    basePrice: action.basePrice || 0,
                    unitPrice: unitPrice?.toString() || '',
                    // A row without a TioTandvård price (0) falls back to the regular price, as on the server.
                    tiotandvardPrice: action.tiotandvardPrice
                        ? action.tiotandvardPrice.toString()
                        : action.price?.toString() || '',
                    discountedPrice: action.discountedPrice,
                    customerSpecificTiotandvardPrice: action.customerSpecificTiotandvardPrice,
                    price: action.price,
                    vatPercent: action.vat?.toString() || '0',
                    priceWithoutVat: unitPrice || 0,
                    priceWithVat: action.vat && action.vat > 0 ? unitPrice * (1 + action.vat / 100) : unitPrice || 0,
                    category: action.category || '',
                    originalPrice: actionPriceLimit(
                        action.originalPrice ? action.originalPrice : action.basePrice ?? 0,
                        isTioTandvard,
                        action.tiotandvardPrice,
                        action.customerSpecificTiotandvardPrice,
                    ),
                    originalVat: action.vat || 0,
                    quotable: action.quotable,
                    costPerTooth: action.costPerTooth,
                } as ActionGridInput;
                setHasChangedOnce(true);
                formik.setValues(newValues, true);
                if (onChange) {
                    onChange(
                        { ...newValues, isValid: true },
                        action.actionMaterials,
                        action.isActionMaterialRequired,
                        action.materialMacroOptions,
                    );
                }
            }
        }
    };

    const handleDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        if (onChange) {
            onChange(emptyActionInput(), [], false);
        }
    };

    const priceListAsOptions = () => {
        if (priceList && priceList.length > 0) {
            return priceList
                .map((action) => {
                    const internalCodeSuffix = action.internalCode ? ` (${action.internalCode})` : '';
                    return {
                        id: action.action || '',
                        key: `${action.actionCode || ''}${internalCodeSuffix}`,
                        value: `${action.description} (${action.category})`,
                    };
                })
                .sort((a, b) => a.key.localeCompare(b.key));
        }
        return [];
    };

    const handleDiscountChange = (discountPercent: string) => {
        const { unitPrice, quantity, vatPercent } = formik.values;
        const priceNum = toNumber(unitPrice);
        const quantityNum = toNumber(quantity);
        const vatMultiplier = toNumber(vatPercent) / 100 + 1;

        let newUnitPrice: string;

        if (discountPercent.trim() === '') {
            // No discount → reset to original price
            newUnitPrice = priceNum.toFixed(2);
        } else {
            const percent = parseFloat(discountPercent);
            if (!isNaN(percent) && percent >= 0 && percent <= 100) {
                const discountedPrice = priceNum * (1 - percent / 100);
                newUnitPrice = discountedPrice.toFixed(2);
            } else {
                // Invalid discount → keep old price
                newUnitPrice = priceNum.toFixed(2);
            }
        }

        const priceWithoutVat = +(parseFloat(newUnitPrice) * quantityNum).toFixed(2);
        const priceWithVat = +(priceWithoutVat * vatMultiplier).toFixed(2);

        const updatedValues = {
            ...formik.values,
            unitPrice: newUnitPrice,
            priceWithoutVat,
            priceWithVat,
        };
        formik.setValues(updatedValues);
        if (onChange) {
            onChange({ ...updatedValues, isValid: formik.isValid }, [], false);
        }
    };

    const handleFieldChange = (field: keyof ActionGridInput) => (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange(e);
        if (!hasChangedOnce) {
            // If this is the first change set default values and trigger onChange
            setHasChangedOnce(true);
            formik.values.quantity = String(defaultQuantity);
            formik.values.unitPrice = '0';
            formik.values.tiotandvardPrice = '0';
            formik.values.vatPercent = '0';
            if (onChange) {
                onChange({ ...formik.values, [field]: e.target.value, isValid: formik.isValid }, [], false);
            }
        }
    };

    const handleValueChange = (newValues: ActionGridInput) => {
        if (hasChangedOnce && onChange) {
            onChange({ ...newValues, isValid: formik.isValid }, [], false);
        }
    };

    const createTooltipText = (action: ActionGridInput) => {
        if (
            disableForActionBased &&
            !action.quotable &&
            action.originalPrice !== undefined &&
            parseFloat(action.unitPrice) > action.originalPrice
        ) {
            return translate('actions-materials:PRICE_EXCEEDS_ORIGINAL');
        }
        if (!isPlaceholderRow && action.basePrice !== 0 && parseFloat(action.unitPrice) !== action.basePrice) {
            return `${translate('orders:REGULAR_PRICE')}: ${formatCurrency(action.basePrice, currency)}`;
        }
        return undefined;
    };

    return (
        <TableRow
            data-test={dataTest || 'actions-data-grid-row'}
            sx={
                isPlaceholderRow
                    ? {
                          opacity: 0.1,
                      }
                    : undefined
            }
        >
            <DataTableCell>
                <DataTableCodeAutocomplete
                    code={formik.values.actionCode || ''}
                    selected={isCellSelected('action')}
                    onFocus={() => setSelectedCell('action')}
                    onBlur={() => setSelectedCell(undefined)}
                    error={Boolean(formik.errors.actionId)}
                    options={priceListAsOptions()}
                    onSelectValue={handleActionSelectChanged}
                />
            </DataTableCell>
            <DataTableCell readonly={disableForActionBased}>
                {disableForActionBased ? (
                    <DataTableValue>{formik.values.description || ''}</DataTableValue>
                ) : (
                    <DataTableTextField
                        autoComplete="off"
                        variant="standard"
                        onFocus={() => setSelectedCell('description')}
                        fullWidth
                        InputProps={{ disableUnderline: true }}
                        selected={isCellSelected('description')}
                        error={formik.touched.description && Boolean(formik.errors.description)}
                        {...formik.getFieldProps('description')}
                        onChange={handleFieldChange('description')}
                        onBlur={(e) => {
                            formik.getFieldProps('description').onBlur(e);
                            setSelectedCell(undefined);
                            handleValueChange(formik.values);
                        }}
                    />
                )}
            </DataTableCell>
            <DataTableCell readonly>
                <DataTableValue>{action.category || ''}</DataTableValue>
            </DataTableCell>
            {quantityFollowsTeeth(formik.values, disableForActionBased) ? (
                // The teeth decide the quantity of a per-tooth action-set action; the server recounts it.
                // The lab's own rows stay editable (DD-3766).
                <DataTableCell readonly>
                    <DataTableValue alignRight>{formik.values.quantity}</DataTableValue>
                </DataTableCell>
            ) : (
                <DataTableCell>
                    <DataTableTextField
                        autoComplete="off"
                        variant="standard"
                        onFocus={() => setSelectedCell('quantity')}
                        fullWidth
                        InputProps={{ disableUnderline: true }}
                        selected={isCellSelected('quantity')}
                        alignRight
                        error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                        {...formik.getFieldProps('quantity')}
                        onChange={handleFieldChange('quantity')}
                        onBlur={(e) => {
                            formik.getFieldProps('quantity').onBlur(e);
                            setSelectedCell(undefined);
                            handleValueChange(formik.values);
                        }}
                    />
                </DataTableCell>
            )}
            <DataTableCell>
                {warranty ? (
                    <DataTableValue alignRight>{isPlaceholderRow ? '' : formik.values.unitPrice}</DataTableValue>
                ) : (
                    <DataTableTextField
                        autoComplete="off"
                        variant="standard"
                        tooltipText={createTooltipText(formik.values)}
                        onFocus={() => setSelectedCell('unitPrice')}
                        fullWidth
                        InputProps={{ disableUnderline: true }}
                        selected={isCellSelected('unitPrice')}
                        alignRight
                        error={formik.touched.unitPrice && Boolean(formik.errors.unitPrice)}
                        {...formik.getFieldProps('unitPrice')}
                        onChange={handleFieldChange('unitPrice')}
                        onBlur={(e) => {
                            formik.getFieldProps('unitPrice').onBlur(e);
                            setSelectedCell(undefined);
                            handleValueChange(formik.values);
                        }}
                    />
                )}
            </DataTableCell>
            <DataTableCell readonly>
                {!isPlaceholderRow && <DataTableValue alignRight>{totalExclVat}</DataTableValue>}
            </DataTableCell>
            <DataTableCell readonly={disableForActionBased}>
                {!isPlaceholderRow && !disableForActionBased ? (
                    <DataTableAutocomplete
                        selected={isCellSelected('vatPercent')}
                        onFocus={() => setSelectedCell('vatPercent')}
                        onBlur={(e) => {
                            formik.getFieldProps('vatPercent').onBlur(e);
                            setSelectedCell(undefined);
                            handleValueChange(formik.values);
                        }}
                        options={vatOptions}
                        onSelectValue={(value) => {
                            const newValues = { ...formik.values, vatPercent: value?.key || '0' };
                            formik.setFieldValue('vatPercent', value?.key || '0');
                            handleValueChange(newValues);
                        }}
                        selectedValue={{ key: formik.values.vatPercent || '0' }}
                        width="100px"
                    />
                ) : !isPlaceholderRow ? (
                    <DataTableValue alignRight>{formik.values.vatPercent || '0'}</DataTableValue>
                ) : undefined}
            </DataTableCell>
            <DataTableCell readonly>
                {!isPlaceholderRow && <DataTableValue alignRight>{totalIncVat}</DataTableValue>}
            </DataTableCell>
            <DataTableCell sx={{ textAlign: 'center' }}>
                {!isPlaceholderRow && (
                    <Fragment>
                        <DiscountButton onChange={handleDiscountChange} />
                        <IconButton
                            onClick={() => setDeleteConfirmOpen(true)}
                            size="small"
                            data-test="actions-data-grid-row.delete-action"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                        <ConfirmDialog
                            open={deleteConfirmOpen}
                            handleClose={() => setDeleteConfirmOpen(false)}
                            dialogContent={translate('actions-materials:CONFIRM_DELETE_ACTION')}
                            handleConfirm={handleDeleteConfirm}
                        />
                    </Fragment>
                )}
            </DataTableCell>
        </TableRow>
    );
};

export default ActionsDataGridRow;
